import React from 'react';
import { ArrowLeft, Info, Droplet, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import FuelPricesTable from '../components/FuelPricesTable';
import LogisticsTips from '../components/LogisticsTips';
import TruckLogo from '../components/TruckLogo';
import BusinessFooter from '../components/BusinessFooter';

const AdditionalInfoPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-primary text-white shadow-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center">
            <Link 
              to="/"
              className="mr-4 p-2 rounded-full hover:bg-primary-dark transition-colors"
              aria-label="Volver"
            >
              <ArrowLeft size={24} />
            </Link>
            <div className="flex items-center">
              <TruckLogo size={28} className="text-white mr-2" />
              <h1 className="text-xl font-bold uppercase">COTIZATRIN</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6 flex items-center">
            <Info className="mr-2 text-blue-500 flex-shrink-0" size={28} />
            Información Adicional
          </h1>
          
          <div className="prose max-w-none text-sm sm:text-base">
            <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-blue-700">
                En esta sección encontrarás información complementaria que te será útil durante el proceso de cotización
                de tus servicios de transporte de mercancía pesada.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-md p-4">
                <h2 className="text-xl font-semibold mb-4 flex items-center text-primary">
                  <Droplet className="mr-2 flex-shrink-0" size={24} />
                  Precios de Combustibles
                </h2>
                <div className="bg-gray-50 rounded-lg p-2 sm:p-4">
                  <p className="text-gray-700 mb-4">
                    Consulta los precios actualizados de los diferentes tipos de combustible para
                    planificar tus costos de transporte con mayor precisión.
                  </p>
                  <FuelPricesTable />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-4">
                <h2 className="text-xl font-semibold mb-4 flex items-center text-primary">
                  <Award className="mr-2 flex-shrink-0" size={24} />
                  Consejos de Logística
                </h2>
                <div className="bg-gray-50 rounded-lg p-2 sm:p-4">
                  <p className="text-gray-700 mb-4">
                    Descubre consejos útiles para optimizar tus procesos logísticos y maximizar
                    la eficiencia de tus operaciones de transporte.
                  </p>
                  <LogisticsTips />
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 sm:p-6 rounded-lg border border-gray-200 mb-8">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">¿Cómo utilizar esta información?</h2>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    1
                  </div>
                  <p className="text-gray-700">
                    <strong>Precios de combustible:</strong> Utiliza los precios actualizados de combustible como 
                    referencia para tus cálculos de costos de transporte. Estos valores afectan directamente el 
                    costo total de tu servicio.
                  </p>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    2
                  </div>
                  <p className="text-gray-700">
                    <strong>Consejos de logística:</strong> Implementa las recomendaciones y mejores prácticas
                    para optimizar la planificación de rutas, gestión de carga y otros aspectos críticos de tus
                    operaciones de transporte.
                  </p>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    3
                  </div>
                  <p className="text-gray-700">
                    <strong>Actualizaciones periódicas:</strong> Revisa esta información regularmente para 
                    mantenerte al día con los cambios en los precios de combustible y nuevos consejos que 
                    puedan beneficiar tu negocio.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-center mb-8">
          <Link
            to="/"
            className="px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300 flex items-center"
          >
            <ArrowLeft className="mr-2" />
            Volver al Formulario
          </Link>
        </div>
      </main>

      <BusinessFooter variant="minimal" />
    </div>
  );
};

export default AdditionalInfoPage;